<script setup lang="ts">
import { ref, computed, type Component } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAppStore, type NavContext } from '@/core/stores/useAppStore'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { PERMISSIONS } from '@/core/permissions/constants'
import { useNotificationSlideOver } from '@/modules/notifications/composables/useNotificationSlideOver'
import {
  IconLayoutDashboard,
  IconFolders,
  IconUsers,
  IconFileText,
  IconShoppingBag,
  IconAddressBook,
  IconFileInvoice,
  IconMail,
  IconRobot,
  IconX,
  IconChevronDown,
  IconLogout,
} from '@tabler/icons-vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const appStore = useAppStore()
const authStore = useAuthStore()
const projectsStore = useProjectsStore()
const notifSlide = useNotificationSlideOver()

// --- Data types ---
interface FlyoutChild {
  label: string
  path?: string
  action?: 'notifSlide'
  permission?: string
  projectScoped?: boolean
  sectionLabel?: string
}

interface NavItem {
  id: NavContext
  icon: Component
  label: string
  permission?: string
  projectScoped?: boolean
  directRoute?: string
  children?: FlyoutChild[]
}

// --- Navigation items (same as rail) ---
const allItems: NavItem[] = [
  { id: 'dashboard', icon: IconLayoutDashboard, label: 'common.nav.dashboard', directRoute: '/' },
  { id: 'projects', icon: IconFolders, label: 'common.nav.projects', permission: PERMISSIONS.PROJECT.LIST, directRoute: '/projects' },
  { id: 'users', icon: IconUsers, label: 'common.nav.users', permission: PERMISSIONS.USER.LIST, directRoute: '/users' },
  {
    id: 'content', icon: IconFileText, label: 'common.nav.content',
    permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.pages', path: '/pages', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.menus', path: '/menus', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.videos', path: '/videos', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.categories', path: '/categories', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.mediaLibrary', path: '/assets', permission: PERMISSIONS.MEDIA.VIEW, projectScoped: true },
      { label: 'common.nav.forms', path: '/forms', permission: PERMISSIONS.SETTINGS.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'sales', icon: IconShoppingBag, label: 'common.nav.sales',
    permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true,
    children: [
      { sectionLabel: 'common.nav.catalog', label: 'common.nav.products', path: '/products', permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true },
      { label: 'common.nav.aurum', path: '/aurum', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
      { label: 'common.nav.aurumCheckIn', path: '/aurum/check-in', permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true },
      { label: 'common.nav.categories', path: '/categories', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { sectionLabel: 'common.nav.shop', label: 'common.nav.orders', path: '/orders', permission: PERMISSIONS.ESHOP.ORDERS, projectScoped: true },
      { label: 'common.nav.customers', path: '/customers', permission: PERMISSIONS.ESHOP.CUSTOMERS, projectScoped: true },
      { label: 'common.nav.tickets', path: '/tickets', permission: PERMISSIONS.TICKETS.LIST, projectScoped: true },
      { sectionLabel: 'common.nav.training', label: 'common.nav.eventDates', path: '/event-dates', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
    ],
  },
  {
    id: 'contacts', icon: IconAddressBook, label: 'common.nav.contacts',
    permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.contactPersons', path: '/contacts', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.groups', path: '/contact-groups', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'invoicing', icon: IconFileInvoice, label: 'common.nav.invoicing',
    permission: PERMISSIONS.INVOICING.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.issuedInvoices', path: '/invoicing/invoices', permission: PERMISSIONS.INVOICING.VIEW, projectScoped: true },
      { label: 'common.nav.clientDirectory', path: '/invoicing/clients', permission: PERMISSIONS.INVOICING.CLIENTS, projectScoped: true },
    ],
  },
  {
    id: 'comms', icon: IconMail, label: 'common.nav.communications',
    projectScoped: true,
    children: [
      { label: 'common.nav.sendNotification', action: 'notifSlide', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
      { label: 'common.nav.emailTemplates', path: '/email-templates', permission: PERMISSIONS.SETTINGS.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'ai', icon: IconRobot, label: 'common.nav.aiAutomation',
    children: [
      { label: 'common.nav.orchestration', path: '/orchestration', permission: PERMISSIONS.ORCHESTRATION.VIEW },
    ],
  },
]

// --- Permission helpers ---
function hasPerm(permission: string, projectScoped = false) {
  if (projectScoped) {
    return authStore.hasPermission(permission, projectsStore.currentProjectPermissions)
  }
  return authStore.hasPermission(permission)
}

// --- Filtered items ---
const filteredItems = computed(() =>
  allItems.filter(item => {
    if (item.projectScoped && !projectsStore.currentProject) return false
    if (item.id === 'invoicing' && !projectsStore.currentProject?.invoicingEnabled) return false
    if (item.permission) return hasPerm(item.permission, !!item.projectScoped)
    return true
  })
)

function visibleChildren(item: NavItem): FlyoutChild[] {
  if (!item.children) return []
  return item.children.filter(child => {
    if (!child.permission) return true
    return hasPerm(child.permission, !!child.projectScoped)
  })
}

// --- Accordion ---
const expanded = ref<Set<string>>(new Set())

function toggleSection(id: string) {
  if (expanded.value.has(id)) {
    expanded.value.delete(id)
  } else {
    expanded.value.add(id)
  }
}

// --- Project selector ---
const showProjects = ref(false)

function selectProject(projectId: string) {
  const full = projectsStore.items.find(p => p.id === projectId)
  if (full) {
    projectsStore.setCurrentProject(full)
    router.push('/')
    closeMobileMenu()
  }
  showProjects.value = false
}

// --- Navigation ---
function isActive(path: string) {
  return route.path === path || route.path.startsWith(`${path}/`)
}

function navigate(path: string) {
  router.push(path)
  closeMobileMenu()
}

function handleChildClick(child: FlyoutChild) {
  if (child.action === 'notifSlide') {
    notifSlide.open()
    closeMobileMenu()
    return
  }
  if (child.path) {
    navigate(child.path)
  }
}

function handleItemClick(item: NavItem) {
  if (item.children?.length) {
    toggleSection(item.id)
    return
  }
  if (item.directRoute) {
    navigate(item.directRoute)
    return
  }
  appStore.setActiveContext(item.id)
}

function closeMobileMenu() {
  if (appStore.isMobileMenuOpen) {
    appStore.toggleMobileMenu()
  }
}

// --- Env / user ---
const userInitial = computed(() =>
  authStore.user?.name?.charAt(0).toUpperCase() || 'U'
)

const envInfo = computed(() => {
  const hostname = window.location.hostname
  let envLabel = 'PROD'
  let dotColor = 'bg-green-400'
  let textColor = 'text-green-400'

  if (hostname.includes('.local') || hostname === 'localhost') {
    envLabel = 'DEV'
    dotColor = 'bg-blue-400'
    textColor = 'text-blue-400'
  } else if (hostname.includes('.test.')) {
    envLabel = 'TEST'
    dotColor = 'bg-purple-400'
    textColor = 'text-purple-400'
  } else if (hostname.includes('.staging.')) {
    envLabel = 'STG'
    dotColor = 'bg-yellow-400'
    textColor = 'text-yellow-400'
  }

  return { envLabel, dotColor, textColor }
})
</script>

<template>
  <div class="w-[280px] max-w-[100vw] h-full bg-base-400 border-r border-white/[0.06] flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
      <div class="flex items-center gap-3">
        <div class="w-[34px] h-[34px] rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center font-display font-bold text-sm text-amber-400">
          {{ projectsStore.currentProject?.name?.charAt(0)?.toUpperCase() || '?' }}
        </div>
        <span class="text-sm font-semibold text-gray-200">Nexus</span>
      </div>
      <button class="rail-btn !w-8 !h-8" @click="closeMobileMenu()">
        <IconX class="w-4 h-4" />
      </button>
    </div>

    <!-- Project selector -->
    <div v-if="projectsStore.currentProject" class="px-4 py-3 border-b border-white/[0.06]">
      <div class="text-2xs font-semibold uppercase tracking-widest text-gray-500 mb-2">{{ t('common.nav.projectLabel') }}</div>
      <button
        class="w-full px-2.5 py-2 bg-base-300 border border-white/[0.06] rounded-md flex items-center gap-2 cursor-pointer hover:border-white/10 transition-colors"
        @click="showProjects = !showProjects"
      >
        <span
          class="w-2 h-2 rounded-full flex-shrink-0"
          :class="projectsStore.currentProject.isActive ? 'bg-amber-400' : 'bg-red-500'"
        ></span>
        <span class="text-sm font-medium text-gray-200 flex-1 text-left truncate">
          {{ projectsStore.currentProject.name }}
        </span>
        <IconChevronDown
          class="w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform duration-200"
          :class="{ 'rotate-180': showProjects }"
        />
      </button>

      <!-- Inline project list -->
      <div v-if="showProjects" class="mt-2 space-y-0.5">
        <button
          v-for="pw in projectsStore.projectsWithPermissions"
          :key="pw.project.id"
          class="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm cursor-pointer transition-colors duration-100"
          :class="projectsStore.currentProject?.id === pw.project.id
            ? 'bg-amber-400/10 text-amber-400'
            : 'text-gray-400 hover:bg-base-200 hover:text-gray-200'"
          @click="selectProject(pw.project.id)"
        >
          <span
            class="w-2 h-2 rounded-full flex-shrink-0"
            :class="pw.project.isActive ? 'bg-amber-400' : 'bg-red-500'"
          ></span>
          <span class="truncate">{{ pw.project.name }}</span>
        </button>
      </div>
    </div>

    <!-- Nav items -->
    <div class="flex-1 overflow-y-auto p-2 space-y-0.5">
      <template v-for="item in filteredItems" :key="item.id">
        <!-- Direct link or accordion header -->
        <a
          class="nav-item gap-3"
          :class="{
            active: item.directRoute && isActive(item.directRoute),
            'font-medium text-gray-200': item.children?.length && expanded.has(item.id),
          }"
          @click.prevent="handleItemClick(item)"
        >
          <component :is="item.icon" class="w-[18px] h-[18px] flex-shrink-0" />
          <span class="flex-1">{{ t(item.label) }}</span>
          <IconChevronDown
            v-if="item.children?.length"
            class="w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform duration-200"
            :class="{ 'rotate-180': expanded.has(item.id) }"
          />
        </a>

        <!-- Children -->
        <div v-if="item.children?.length && expanded.has(item.id)" class="pl-7 space-y-0.5">
          <template v-for="(child, idx) in visibleChildren(item)" :key="idx">
            <div v-if="child.sectionLabel" class="text-2xs font-bold uppercase tracking-wider text-gray-500/50 px-3 pt-3 pb-1">
              {{ t(child.sectionLabel) }}
            </div>
            <a
              class="nav-item"
              :class="{ active: child.path && isActive(child.path) }"
              @click.prevent="handleChildClick(child)"
            >
              {{ t(child.label) }}
            </a>
          </template>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div class="px-4 py-3 border-t border-white/[0.06]">
      <div class="flex items-center gap-3 mb-3">
        <div
          class="w-[30px] h-[30px] rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center font-bold text-xs text-amber-400 flex-shrink-0"
        >
          {{ userInitial }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-gray-200 truncate">{{ authStore.user?.name || 'User' }}</div>
          <div class="text-xs text-gray-500 truncate">{{ authStore.user?.email || '' }}</div>
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full" :class="envInfo.dotColor"></span>
          <span class="text-2xs font-bold tracking-wider" :class="envInfo.textColor">{{ envInfo.envLabel }}</span>
        </div>
        <button
          class="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 cursor-pointer transition-colors"
          @click="authStore.logout()"
        >
          <IconLogout class="w-3.5 h-3.5" />
          {{ t('common.nav.logout') }}
        </button>
      </div>
    </div>
  </div>
</template>
